import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { Lightbox } from 'ngx-lightbox';
import { merge, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { FilterEnabledComponent } from 'src/app/helpers/filter-enabled.component';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import {
  BoardTypeChoiceArray,
  BoardTypeChoices,
} from 'src/app/model/product/board/board-part';
import { IPBoardPlacementTypeChoicesArray } from 'src/app/model/product/ip-board/ip-board-placement-type';
import { ProperPageResult } from 'src/app/model/proper-page-result';
import { SignMeasurementsChoiceArray } from 'src/app/model/sign/enums/sign-measurements';
import { SignPlacementPrice } from 'src/app/model/sign/sign-placement-price/sign-placement-price';
import {
  SIGN_PLACEMENT_PRICE_TYPE_CHOICES,
  SIGN_PLACEMENT_PRICE_TYPE_CHOICES_ARRAY,
} from 'src/app/model/sign/sign-placement-price/sign-placement-price-type';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import { ModalService } from 'src/app/services/modal.service';
import {
  PageIndexService,
  PAGE_SIZE_OPTIONS,
} from 'src/app/services/page-index-service';
import { SnackService } from 'src/app/services/snack.service';
import { SignPlacementPriceControllerRequestService } from './service/sign-placement-price-controller-request.service';
import {
  DOUBLE_SIDED_PLACEMENT_CHOICES_ARRAY,
  IP_BOARD_SIGN_MEASUREMENTS_CHOICE_ARRAY,
  SignPlacementPriceDetailHelper,
} from './sign-placement-price-detail-helper';

@Component({
  selector: 'sign-placement-price-detail',
  templateUrl: './sign-placement-price-detail.component.html',
  styleUrls: ['./sign-placement-price-detail.component.scss'],
})
export class SignPlacementPriceDetailComponent
  extends FilterEnabledComponent
  implements OnInit, OnDestroy
{
  totalItems = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  loading: boolean = true;
  signPlacementPrices: SignPlacementPrice[];

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  SIGN_PLACEMENT_PRICE_TYPE_CHOICES = SIGN_PLACEMENT_PRICE_TYPE_CHOICES;

  SIGN_PLACEMENT_PRICE_TYPE_CHOICES_ARRAY =
    SIGN_PLACEMENT_PRICE_TYPE_CHOICES_ARRAY;
  SIGN_MEASUREMENTS_CHOICES_ARRAY = SignMeasurementsChoiceArray;
  IP_BOARD_SIGN_MEASUREMENTS_CHOICES_ARRAY =
    IP_BOARD_SIGN_MEASUREMENTS_CHOICE_ARRAY;
  BOARD_TYPE_CHOICES = BoardTypeChoices;
  BOARD_TYPE_CHOICE_ARRAY = BoardTypeChoiceArray;
  PLACEMENT_TYPE_CHOICES_ARRAY = IPBoardPlacementTypeChoicesArray;
  DOUBLE_SIDED_PLACEMENT_CHOICES_ARRAY = DOUBLE_SIDED_PLACEMENT_CHOICES_ARRAY;

  private reload = new Subject<any>();
  private destroy = new Subject<any>();

  constructor(
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    private signPlacementPriceControllerRequestService: SignPlacementPriceControllerRequestService,
    private modalService: ModalService,
    private snackService: SnackService,
    private _lightbox: Lightbox
  ) {
    super([], filterStoreService, pageIndexService);
  }

  ngOnInit(): void {
    this.paginator.pageIndex =
      this.initPageIndex == null ? 0 : this.initPageIndex;
    this.paginator.pageSize =
      this.initPageSize == null ? 30 : this.initPageSize;

    const loadObservable = merge(this.reload, this.paginator.page).pipe(
      startWith({}),
      switchMap(() => {
        this.loading = true;
        this.pageIndexService.setPageIndex(
          this.getLocalStorageKey(),
          this.paginator.pageIndex
        );
        this.pageIndexService.setPageSize(
          this.getLocalStorageKey(),
          this.paginator.pageSize
        );
        return this.signPlacementPriceControllerRequestService.signPlacementPrices(
          this.generateFilterString(),
          null,
          this.paginator.pageSize,
          this.paginator.pageIndex
        );
      }),
      catchError(() => {
        return of({});
      }),
      takeUntil(this.destroy)
    );

    this.addSubscription(
      loadObservable,
      (res: ProperPageResult<SignPlacementPrice>) => {
        this.signPlacementPrices = res == null ? [] : res.items;
        this.preprocessSignPlacementPrices();
        this.totalItems = res.totalItems;
        this.loading = false;
      }
    );

    this.addSubscription(this.filtersChanged.pipe(debounceTime(800)), () => {
      this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
      this.paginator.pageIndex = 0;
      this.reload.next();
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.reload != null) {
      this.reload.complete();
    }
    if (this.destroy != null) {
      this.destroy.next();
      this.destroy.complete();
    }
  }

  signPlacementPriceTypeChanged(signPlacementPrice: SignPlacementPrice): void {
    signPlacementPrice.signMeasurements = null;
    signPlacementPrice.boardType = null;
    signPlacementPrice.placementType = null;
    SignPlacementPriceDetailHelper.calculateDesign(signPlacementPrice);
  }

  boardTypeChanged(signPlacementPrice: SignPlacementPrice): void {
    signPlacementPrice.placementType = null;
    SignPlacementPriceDetailHelper.calculateDesign(signPlacementPrice);
  }

  placementTypeChanged(signPlacementPrice: SignPlacementPrice): void {
    SignPlacementPriceDetailHelper.calculateDesign(signPlacementPrice);
  }

  private preprocessSignPlacementPrices(): void {
    if (CollectionHelper.isPopulated(this.signPlacementPrices)) {
      this.signPlacementPrices.forEach((p) => {
        SignPlacementPriceDetailHelper.calculateDesign(p);
      });
    }
  }

  addSignPlacementPrice(): void {
    const newPrice = new SignPlacementPrice();
    newPrice.editing = true;
    this.signPlacementPrices.push(newPrice);
  }

  openLightbox(url: string): void {
    this._lightbox.open([
      {
        src: url,
        thumb: url,
      },
    ]);
  }

  toggleEditing(signPlacementPrice: SignPlacementPrice): void {
    signPlacementPrice.editing = GeneralHelper.invertNullableBoolean(
      signPlacementPrice.editing
    );
  }

  saveSignPlacementPrice(
    signPlacementPrice: SignPlacementPrice,
    index: number
  ): void {
    if (this.verifyPlacementPrice(signPlacementPrice)) {
      this.doSaveSignPlacementPrice(signPlacementPrice, index);
    }
  }

  private async doSaveSignPlacementPrice(
    signPlacementPrice: SignPlacementPrice,
    index: number
  ) {
    this.loading = true;
    try {
      const savePricePromise =
        signPlacementPrice.id == null
          ? this.signPlacementPriceControllerRequestService.createSignPlacementPrice(
              signPlacementPrice
            )
          : this.signPlacementPriceControllerRequestService.updateSignPlacementPrice(
              signPlacementPrice
            );
      const savedPrice = await savePricePromise;
      SignPlacementPriceDetailHelper.calculateDesign(savedPrice);
      this.signPlacementPrices[index] = savedPrice;
      this.snackService.showSuccess('Plaatsingsprijs succesvol opgeslagen');
    } finally {
      this.loading = false;
    }
  }

  deleteSignPlacementPrice(
    signPlacementPrice: SignPlacementPrice,
    index: number
  ): void {
    this.modalService.confirmDialog(
      'Plaatsingsprijs verwijderen',
      'Bent u zeker dat u deze plaatsingsprijs wilt verwijderen?',
      (b: boolean) => {
        if (b) {
          this.doDeleteSignPlacementPrice(signPlacementPrice, index);
        }
      }
    );
  }

  private async doDeleteSignPlacementPrice(
    signPlacementPrice: SignPlacementPrice,
    index: number
  ): Promise<void> {
    this.loading = true;
    try {
      await this.signPlacementPriceControllerRequestService.removeSignPlacementPrice(
        signPlacementPrice
      );
      this.signPlacementPrices.splice(index, 1);
      this.snackService.showSuccess('Plaatsingsprijs succesvol verwijderd');
    } finally {
      this.loading = false;
    }
  }

  private verifyPlacementPrice(
    signPlacementPrice: SignPlacementPrice
  ): boolean {
    let errorMessage: string = null;
    if (signPlacementPrice.signPlacementPriceType == null) {
      errorMessage = "Gelieve een keuze te maken voor 'Naam bord'";
    } else if (signPlacementPrice.signMeasurements == null) {
      errorMessage = "Gelieve een keuze te maken voor 'Afmetingen'";
    } else if (
      signPlacementPrice.signPlacementPriceType.ordinal ===
        SIGN_PLACEMENT_PRICE_TYPE_CHOICES.IP_BOARD.ordinal &&
      signPlacementPrice.boardType == null
    ) {
      errorMessage = "Gelieve een keuze te maken voor 'Type bord'";
    } else if (
      signPlacementPrice.signPlacementPriceType.ordinal ===
        SIGN_PLACEMENT_PRICE_TYPE_CHOICES.IP_BOARD.ordinal &&
      signPlacementPrice.placementType == null
    ) {
      errorMessage = "Gelieve een keuze te maken voor 'Type plaatsing'";
    } else if (signPlacementPrice.placementMinuteBudget == null) {
      errorMessage = "Gelieve een waarde in te vullen voor 'Tijd plaatsing'";
    } else if (signPlacementPrice.repoMinuteBudget == null) {
      errorMessage = "Gelieve een waarde in te vullen voor 'Tijd afhalen'";
    } else if (signPlacementPrice.priceAAA == null) {
      errorMessage = 'Gelieve een prijs in te vullen voor AAA-klanten';
    } else if (signPlacementPrice.priceAA == null) {
      errorMessage = 'Gelieve een prijs in te vullen voor AA-klanten';
    } else if (signPlacementPrice.priceA == null) {
      errorMessage = 'Gelieve een prijs in te vullen voor A-klanten';
    } else if (signPlacementPrice.priceB == null) {
      errorMessage = 'Gelieve een prijs in te vullen voor B-klanten';
    } else if (signPlacementPrice.priceC == null) {
      errorMessage = 'Gelieve een prijs in te vullen voor C-klanten';
    }

    if (errorMessage != null) {
      this.snackService.showError(errorMessage);
    }
    return errorMessage == null;
  }

  compareOrdinalEnums(o1: any, o2: any): boolean {
    return GeneralHelper.compareOrdinalEnum(o1, o2);
  }

  compareValueEnums(o1: any, o2: any): boolean {
    return GeneralHelper.compareValueEnum(o1, o2);
  }

  protected getLocalStorageKey(): string {
    return 'SignPlacementPriceDetailComponent';
  }
}
